import React, { useEffect, useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { DashboardCard } from '../components/dashboard/DashboardCard';
import { CardBrandsTable } from '../components/dashboard/CardBrandsTable';
import { CardBrandsChart } from '../components/dashboard/CardBrandsChart';
import { KpiCard } from '../components/dashboard/KpiCard';
import { TabView } from '../components/dashboard/TabView';
import { GeoPaymentMap } from '../components/dashboard/GeoPaymentMap';
import { CardErrorCodes } from '../components/dashboard/CardErrorCodes';
import { CompanySelector, Company } from '../components/dashboard/CompanySelector';
import { CreditCardIcon, ArrowUpRightIcon, ArrowDownRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, BuildingIcon, FilterIcon, RotateCcwIcon, ClockIcon as PendingIcon, AlertCircleIcon, ReceiptIcon, RefreshCcwIcon, X } from 'lucide-react';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData } from '../components/dashboard/DataGenerator';
export function Overview() {
  const [timeRange, setTimeRange] = useState('7d');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [paymentVolumeData, setPaymentVolumeData] = useState([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState([]);
  const [transactionCountData, setTransactionCountData] = useState([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState([]);
  const [averageTransactionData, setAverageTransactionData] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [attemptsData, setAttemptsData] = useState([]);
  const [geoPaymentData, setGeoPaymentData] = useState([]);
  const [refundData, setRefundData] = useState([]);
  const [previousRefundData, setPreviousRefundData] = useState([]);
  const [pendingRefundsData, setPendingRefundsData] = useState([]);
  const [refundReasonsData, setRefundReasonsData] = useState([]);
  // Load companies on component mount
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    // Default to all companies selected
    setSelectedCompanies(companiesData);
  }, []);
  // Update data when time range or selected companies change
  useEffect(() => {
    // Get raw data
    const rawPaymentVolumeData = generatePaymentVolumeData(timeRange);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, timeRange);
    const rawTransactionCountData = generateTransactionCountData(timeRange);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, timeRange);
    const rawAverageTransactionData = generateAverageTransactionData(timeRange);
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawConversionData = generateConversionData();
    const rawAttemptsData = generateAttemptsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData(timeRange);
    const rawPreviousRefundData = generatePreviousRefundData(rawRefundData, timeRange);
    const rawPendingRefundsData = generatePendingRefundsData();
    const rawRefundReasonsData = generateRefundReasonsData();
    // Filter data by selected companies if needed
    const selectedCompanyIds = selectedCompanies.map(company => company.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length;
    if (isAllCompanies || selectedCompanyIds.length === 0) {
      // Use all data
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setConversionData(rawConversionData);
      setAttemptsData(rawAttemptsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setPreviousRefundData(rawPreviousRefundData);
      setPendingRefundsData(rawPendingRefundsData);
      setRefundReasonsData(rawRefundReasonsData);
    } else {
      // Filter data based on selected companies
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData); // Keep the same proportions
      setConversionData(rawConversionData); // Keep the same proportions
      setAttemptsData(filterDataByCompanies(rawAttemptsData, selectedCompanyIds));
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setPreviousRefundData(filterDataByCompanies(rawPreviousRefundData, selectedCompanyIds));
      setPendingRefundsData(filterDataByCompanies(rawPendingRefundsData, selectedCompanyIds));
      setRefundReasonsData(rawRefundReasonsData); // Keep the same proportions
    }
  }, [timeRange, selectedCompanies, companies]);
  // Calculate total payment volume
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  // Calculate total transactions
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  // Calculate average transaction
  const avgTransaction = totalVolume / totalTransactions;
  // Calculate conversion rate
  const conversionRate = conversionData.length > 2 ? conversionData[2].value / conversionData[0].value * 100 : 0;
  // Calculate percentage of transactions by single attempt
  const singleAttemptRate = attemptsData.length > 0 ? attemptsData[0].count / attemptsData.reduce((sum, item) => sum + item.count, 0) * 100 : 0;
  // Calculate average number of attempts
  const avgAttempts = attemptsData.length > 0 ? attemptsData.reduce((sum, item, index) => {
    const attemptNumber = index === 3 ? 4 : index + 1; // 4+ is counted as 4
    return sum + item.count * attemptNumber;
  }, 0) / attemptsData.reduce((sum, item) => sum + item.count, 0) : 0;
  // Calculate refund metrics
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const totalRefundCount = refundData.reduce((sum, item) => sum + item.count, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  // Format dates based on time range
  const formatDate = (date, timeRange) => {
    if (timeRange === 'today') {
      // For today's data, format as hour
      if (typeof date === 'string' && date.includes('T')) {
        const dateObj = new Date(date);
        return dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      // If we have an hour field, use that
      return date;
    } else if (timeRange === '7d' || timeRange === '30d') {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } else {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    }
  };
  // Get the title for the time period based on selected range
  const getTimeRangeTitle = () => {
    switch (timeRange) {
      case 'today':
        return 'Today (hourly)';
      case '7d':
        return 'Last 7 days';
      case '30d':
        return 'Last 30 days';
      case 'year':
        return 'This year';
      case 'all':
        return 'All time';
      default:
        return 'Selected period';
    }
  };
  // Get the x-axis key for the data
  const getXAxisKey = () => {
    return timeRange === 'today' ? 'hour' : 'date';
  };
  // Table view for payment volume data
  const PaymentVolumeTable = () => <div className="overflow-x-auto" data-id="element-4636">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4637">
        <thead className="bg-gray-50" data-id="element-4638">
          <tr data-id="element-4639">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4640">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4641">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4642">
          {paymentVolumeData.map((row, index) => <tr key={index} data-id="element-4643">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4644">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4645">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for transaction count data
  const TransactionCountTable = () => <div className="overflow-x-auto" data-id="element-4646">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4647">
        <thead className="bg-gray-50" data-id="element-4648">
          <tr data-id="element-4649">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4650">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4651">
              Transactions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4652">
          {transactionCountData.map((row, index) => <tr key={index} data-id="element-4653">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4654">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4655">
                {row.count.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for payment methods data
  const PaymentMethodsTable = () => <div className="overflow-x-auto" data-id="element-4656">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4657">
        <thead className="bg-gray-50" data-id="element-4658">
          <tr data-id="element-4659">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4660">
              Payment Method
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4661">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4662">
          {paymentMethodsData.map((row, index) => <tr key={index} data-id="element-4663">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4664">
                {row.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4665">
                {row.value}%
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for refund data
  const RefundDataTable = () => <div className="overflow-x-auto" data-id="element-4666">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4667">
        <thead className="bg-gray-50" data-id="element-4668">
          <tr data-id="element-4669">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4670">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4671">
              Refund Amount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4672">
              Count
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4673">
          {refundData.map((row, index) => <tr key={index} data-id="element-4674">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4675">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4676">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4677">
                {row.count}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for geographic payment data
  const GeoPaymentTable = () => <div className="overflow-x-auto" data-id="element-4678">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4679">
        <thead className="bg-gray-50" data-id="element-4680">
          <tr data-id="element-4681">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4682">
              Country
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4683">
              Transactions
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4684">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4685">
          {geoPaymentData.map((row, index) => <tr key={index} data-id="element-4686">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4687">
                {row.country}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4688">
                {row.transactions.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4689">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Determine if we're showing aggregated data
  const isAggregated = selectedCompanies.length > 1 && selectedCompanies.length < companies.length;
  return <div className="space-y-6" data-id="element-4690">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-4691">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-4692">
          Dashboard Overview
        </h1>
        <div className="flex flex-col gap-3 w-full md:w-auto" data-id="element-4693">
          {/* Controls row - Time Range Selector first, then Company Selector */}
          <div className="flex flex-wrap items-center gap-3 justify-end" data-id="element-4694">
            {/* Time Range Selector */}
            <TimeSelector value={timeRange} onChange={setTimeRange} data-id="element-4695" />
            {/* Company Selector */}
            {companies.length > 0 && <CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-4696" />}
          </div>
          {/* Selected company tags in a separate row */}
          {selectedCompanies.length > 1 && selectedCompanies.length < companies.length && <div className="flex flex-wrap gap-2 mt-2 justify-end" data-id="element-4697">
                {selectedCompanies.slice(0, 3).map(company => <div key={company.id} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full" data-id="element-4698">
                    {company.name}
                    <button onClick={() => {
              setSelectedCompanies(selectedCompanies.filter(c => c.id !== company.id));
            }} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-4699">
                      <X size={12} data-id="element-4700" />
                    </button>
                  </div>)}
                {selectedCompanies.length > 3 && <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-4701">
                    +{selectedCompanies.length - 3} more
                  </div>}
              </div>}
        </div>
      </div>
      {/* Aggregation indicator */}
      {isAggregated && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start" data-id="element-4702">
          <FilterIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-4703" />
          <div data-id="element-4704">
            <p className="text-sm text-blue-800" data-id="element-4705">
              <span className="font-medium" data-id="element-4706">Aggregated view:</span> Showing
              combined data for {selectedCompanies.length} companies.
            </p>
            <div className="mt-1 flex flex-wrap gap-1" data-id="element-4707">
              {selectedCompanies.map(company => <span key={company.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-4708">
                  {company.name}
                </span>)}
            </div>
          </div>
        </div>}
      {/* First row: Only 4 KPIs in a horizontal row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4709">
        <KpiCard title="Total Volume" value={`Rs. ${(totalVolume / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-4711" />} trend={{
        value: '8.2%',
        isPositive: true
      }} color="blue" data-id="element-4710" />
        <KpiCard title="Transactions" value={totalTransactions.toLocaleString()} icon={<BarChart3Icon size={18} data-id="element-4713" />} trend={{
        value: '5.7%',
        isPositive: true
      }} color="green" data-id="element-4712" />
        <KpiCard title="Avg Transaction" value={`Rs. ${avgTransaction.toFixed(2)}`} icon={<ArrowUpRightIcon size={18} data-id="element-4715" />} trend={{
        value: '2.3%',
        isPositive: true
      }} color="purple" data-id="element-4714" />
        <KpiCard title="Conversion" value={`${conversionRate.toFixed(1)}%`} icon={<CreditCardIcon size={18} data-id="element-4717" />} trend={{
        value: '1.4%',
        isPositive: false
      }} color="orange" data-id="element-4716" />
      </div>
      {/* Second row: 3-column chart and 1-column stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4718">
        {/* Payment Volume Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col h-full" data-id="element-4719">
          <DashboardCard title="Payment Volume" subtitle={getTimeRangeTitle()} className="h-full flex-grow" data-id="element-4720">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-4722">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-4723">
                        <LineChart data={[...paymentVolumeData, ...previousPaymentVolumeData]} data-id="element-4724">
                          <CartesianGrid strokeDasharray="3 3" data-id="element-4725" />
                          <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4726" />
                          <YAxis tickFormatter={value => `Rs. ${value / 1000}k`} data-id="element-4727" />
                          <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Volume']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4728" />
                          <Legend data-id="element-4729" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={2} name="Current Period" connectNulls dot={{
                    r: 2
                  }} activeDot={{
                    r: 6
                  }} data={paymentVolumeData} data-id="element-4730" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousPaymentVolumeData} data-id="element-4731" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
          }, {
            label: 'Data',
            content: <PaymentVolumeTable data-id="element-4732" />
          }]} data-id="element-4721" />
          </DashboardCard>
        </div>
        {/* Stacked KPIs (1 column) */}
        <div className="flex flex-col h-full" data-id="element-4733">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-4734">
            <KpiCard title="Daily Average" value={`Rs. ${(totalVolume / paymentVolumeData.length / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-4736" />} trend={{
            value: '4.5%',
            isPositive: true
          }} color="blue" data-id="element-4735" />
            <KpiCard title="Monthly Growth" value="12.7%" icon={<ArrowUpRightIcon size={18} data-id="element-4738" />} trend={{
            value: '3.2%',
            isPositive: true
          }} color="green" data-id="element-4737" />
            <KpiCard title="YoY Growth" value="24.3%" icon={<BarChart3Icon size={18} data-id="element-4740" />} trend={{
            value: '5.8%',
            isPositive: true
          }} color="purple" data-id="element-4739" />
            <KpiCard title="Transaction Growth" value="18.5%" icon={<ArrowUpRightIcon size={18} data-id="element-4742" />} trend={{
            value: '2.7%',
            isPositive: true
          }} color="orange" data-id="element-4741" />
          </div>
        </div>
      </div>
      {/* Third row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4743">
        {/* Transaction Count Chart (2 columns) */}
        <DashboardCard title="Number of Transactions" subtitle={getTimeRangeTitle()} data-id="element-4744">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-64" data-id="element-4746">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4747">
                      <LineChart data={[...transactionCountData, ...previousTransactionCountData]} data-id="element-4748">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-4749" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4750" />
                        <YAxis data-id="element-4751" />
                        <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4752" />
                        <Legend data-id="element-4753" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={2} name="Current Period" connectNulls dot={{
                  r: 2
                }} activeDot={{
                  r: 6
                }} data={transactionCountData} data-id="element-4754" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousTransactionCountData} data-id="element-4755" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <TransactionCountTable data-id="element-4756" />
        }]} data-id="element-4745" />
        </DashboardCard>
        {/* Average Transaction Chart (2 columns) */}
        <DashboardCard title="Average Transaction" subtitle={`Last ${timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : 'year'}`} data-id="element-4757">
          <div className="h-64" data-id="element-4758">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4759">
              <BarChart data={averageTransactionData} data-id="element-4760">
                <CartesianGrid strokeDasharray="3 3" data-id="element-4761" />
                <XAxis dataKey="date" tickFormatter={date => formatDate(date, timeRange)} data-id="element-4762" />
                <YAxis tickFormatter={value => `Rs. ${value}`} data-id="element-4763" />
                <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Average']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4764" />
                <Bar dataKey="amount" fill="#7FC29B" data-id="element-4765" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Fourth row: Card Brands chart and Success Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4766">
        {/* Card Brands Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col" data-id="element-4767">
          <DashboardCard title="Card Brands" subtitle="Transaction Volume" className="h-full flex-grow" data-id="element-4768">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-4770">
                      <CardBrandsChart data-id="element-4771" />
                    </div>
          }, {
            label: 'Data',
            content: <CardBrandsTable data-id="element-4772" />
          }]} data-id="element-4769" />
          </DashboardCard>
        </div>
        {/* Card Success Rate (1 column) */}
        <div className="flex flex-col" data-id="element-4773">
          <DashboardCard title="Card Success Rate" subtitle="By Brand" className="h-full flex-grow" data-id="element-4774">
            <div className="space-y-3 p-2" data-id="element-4775">
              <div className="flex justify-between items-center" data-id="element-4776">
                <div className="flex items-center" data-id="element-4777">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/visa.svg" alt="Visa" className="h-6 w-10 mr-2" data-id="element-4778" />
                  <span data-id="element-4779">Visa</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4780">98.2%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4781">
                <div className="flex items-center" data-id="element-4782">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/mastercard.svg" alt="MasterCard" className="h-6 w-10 mr-2" data-id="element-4783" />
                  <span data-id="element-4784">MasterCard</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4785">97.5%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4786">
                <div className="flex items-center" data-id="element-4787">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/amex.svg" alt="Amex" className="h-6 w-10 mr-2" data-id="element-4788" />
                  <span data-id="element-4789">Amex</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4790">95.8%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4791">
                <div className="flex items-center" data-id="element-4792">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/discover.svg" alt="Discover" className="h-6 w-10 mr-2" data-id="element-4793" />
                  <span data-id="element-4794">Discover</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4795">96.3%</span>
              </div>
            </div>
            <CardErrorCodes timeRange={timeRange} data-id="element-4796" />
          </DashboardCard>
        </div>
      </div>
      {/* New fifth row: Card Volume KPIs in horizontal layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4797">
        <KpiCard title="Visa Volume" value="Rs. 112.5k" icon={<CreditCardIcon size={18} data-id="element-4799" />} color="blue" data-id="element-4798" />
        <KpiCard title="MC Volume" value="Rs. 87.3k" icon={<CreditCardIcon size={18} data-id="element-4801" />} color="orange" data-id="element-4800" />
        <KpiCard title="Amex Volume" value="Rs. 42.8k" icon={<CreditCardIcon size={18} data-id="element-4803" />} color="green" data-id="element-4802" />
        <KpiCard title="Discover Volume" value="Rs. 21.4k" icon={<CreditCardIcon size={18} data-id="element-4805" />} color="purple" data-id="element-4804" />
      </div>
      {/* Fifth row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4806">
        {/* Payment Methods Chart (2 columns) */}
        <DashboardCard title="Payment Methods" subtitle="Transaction Count" data-id="element-4807">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-4809">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4810">
                      <PieChart data-id="element-4811">
                        <Pie data={paymentMethodsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} fill="#8884d8" dataKey="value" label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-4812">
                          {paymentMethodsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4813" />)}
                        </Pie>
                        <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-4814" />
                        <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-4815" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <PaymentMethodsTable data-id="element-4816" />
        }]} data-id="element-4808" />
        </DashboardCard>
        {/* Payment Conversion Chart (2 columns) */}
        <DashboardCard title="Payment Conversion" subtitle="Success Rate" data-id="element-4817">
          <div className="h-64" data-id="element-4818">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4819">
              <BarChart data={conversionData} data-id="element-4820">
                <CartesianGrid strokeDasharray="3 3" data-id="element-4821" />
                <XAxis dataKey="name" data-id="element-4822" />
                <YAxis data-id="element-4823" />
                <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} data-id="element-4824" />
                <Bar dataKey="value" fill="#3498db" data-id="element-4825">
                  {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4826" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-center text-sm text-gray-600" data-id="element-4827">
            Conversion rate: {conversionRate.toFixed(1)}%
          </div>
        </DashboardCard>
      </div>
      {/* Sixth row: One 2-column chart and 4 vertically stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4828">
        {/* Attempts Per Payment Chart (2 columns) */}
        <div className="flex flex-col h-full" data-id="element-4829">
          <DashboardCard title="Attempts Per Payment" subtitle="Before Success" className="h-full flex-grow" data-id="element-4830">
            <div className="h-80" data-id="element-4831">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4832">
                <BarChart data={attemptsData} data-id="element-4833">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-4834" />
                  <XAxis dataKey="attempts" data-id="element-4835" />
                  <YAxis data-id="element-4836" />
                  <Tooltip formatter={value => [value.toLocaleString(), 'Payments']} data-id="element-4837" />
                  <Bar dataKey="count" fill="#5DAFBA" data-id="element-4838" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center text-sm text-gray-600" data-id="element-4839">
              Average attempts: {avgAttempts.toFixed(1)}
            </div>
          </DashboardCard>
        </div>
        {/* 4 Vertically stacked KPIs */}
        <div className="flex flex-col h-full" data-id="element-4840">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-4841">
            <KpiCard title="First Attempt" value={`${singleAttemptRate.toFixed(1)}%`} icon={<CheckCircleIcon size={18} data-id="element-4843" />} trend={{
            value: '3.1%',
            isPositive: true
          }} color="green" data-id="element-4842" />
            <KpiCard title="Avg Attempts" value={avgAttempts.toFixed(1)} icon={<ClockIcon size={18} data-id="element-4845" />} trend={{
            value: '0.2',
            isPositive: false
          }} color="blue" data-id="element-4844" />
            <KpiCard title="Customers" value="1,243" icon={<UsersIcon size={18} data-id="element-4847" />} trend={{
            value: '12.5%',
            isPositive: true
          }} color="purple" data-id="element-4846" />
            <KpiCard title="Chargeback" value="0.3%" icon={<PercentIcon size={18} data-id="element-4849" />} trend={{
            value: '0.1%',
            isPositive: true
          }} color="orange" data-id="element-4848" />
          </div>
        </div>
      </div>
      {/* New Refunds Row: Header */}
      <div className="mt-8 mb-4" data-id="element-4850">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center" data-id="element-4851">
          <RotateCcwIcon size={20} className="mr-2 text-red-500" data-id="element-4852" />
          Refund Analysis
        </h2>
      </div>
      {/* Refunds KPIs row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4853">
        <KpiCard title="Total Refunds" value={`Rs. ${(totalRefundAmount / 1000).toFixed(1)}k`} icon={<RotateCcwIcon size={18} data-id="element-4855" />} trend={{
        value: '2.1%',
        isPositive: false
      }} color="red" data-id="element-4854" />
        <KpiCard title="Refund Count" value={totalRefundCount} icon={<ReceiptIcon size={18} data-id="element-4857" />} trend={{
        value: '1.5%',
        isPositive: false
      }} color="red" data-id="element-4856" />
        <KpiCard title="Refund Rate" value={`${refundPercentage.toFixed(1)}%`} icon={<PercentIcon size={18} data-id="element-4859" />} trend={{
        value: '0.2%',
        isPositive: true
      }} color="blue" data-id="element-4858" />
        <KpiCard title="Avg Refund" value={`Rs. ${(totalRefundAmount / totalRefundCount).toFixed(2)}`} icon={<DollarSignIcon size={18} data-id="element-4861" />} trend={{
        value: '3.8%',
        isPositive: false
      }} color="orange" data-id="element-4860" />
      </div>
      {/* Refunds charts row - Modified to remove Pending Refunds */}
      <div className="grid grid-cols-1 gap-6" data-id="element-4862">
        {/* Refund Amounts Chart (full width) */}
        <DashboardCard title="Refund Volume" subtitle={getTimeRangeTitle()} icon={<RotateCcwIcon size={18} className="text-red-500 mr-2" data-id="element-4864" />} className="h-full flex-grow" data-id="element-4863">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-4866">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4867">
                      <LineChart data={[...refundData, ...previousRefundData]} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-4868">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-4869" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4870" />
                        <YAxis tickFormatter={value => `Rs. ${value}`} data-id="element-4871" />
                        <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Refund Amount']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4872" />
                        <Legend data-id="element-4873" />
                        <Line type="monotone" dataKey="amount" name="Current Period" stroke="#ef4444" strokeWidth={2} activeDot={{
                  r: 6
                }} connectNulls dot={{
                  r: 2
                }} data={refundData} data-id="element-4874" />
                        <Line type="monotone" dataKey="amount" name="Previous Period" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="5 5" connectNulls dot={false} data={previousRefundData} data-id="element-4875" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <RefundDataTable data-id="element-4876" />
        }]} data-id="element-4865" />
        </DashboardCard>
      </div>
      {/* Refund Reasons */}
      <div data-id="element-4877">
        <DashboardCard title="Refund Reasons" subtitle="Distribution by Category" icon={<RefreshCcwIcon size={18} className="text-blue-500 mr-2" data-id="element-4879" />} data-id="element-4878">
          <div className="h-80" data-id="element-4880">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4881">
              <PieChart data-id="element-4882">
                <Pie data={refundReasonsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} dataKey="count" nameKey="reason" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-4883">
                  {refundReasonsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4884" />)}
                </Pie>
                <Tooltip formatter={value => [value, 'Count']} data-id="element-4885" />
                <Legend data-id="element-4886" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Seventh row: Geographic payment map */}
      <div data-id="element-4887">
        <DashboardCard title="Payment Geography" subtitle="Transaction Origin" icon={<GlobeIcon size={18} className="text-blue-600 mr-2" data-id="element-4889" />} data-id="element-4888">
          <TabView tabs={[{
          label: 'Map',
          content: <div className="h-96" data-id="element-4891">
                    <GeoPaymentMap data={geoPaymentData} data-id="element-4892" />
                  </div>
        }, {
          label: 'Data',
          content: <GeoPaymentTable data-id="element-4893" />
        }]} data-id="element-4890" />
        </DashboardCard>
      </div>
    </div>;
}